import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Chart from 'chart.js/auto';
import { FaChartPie } from "react-icons/fa6";
import { IoBarChart } from "react-icons/io5";
import {
  fmesh,
  fullmesh,
  fmeshPoints,
  foundFeature,
  setFoundFeature,
  animate_speed,
  mapPalette
} from '../../../../core/globalVariables.js';
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { FaExclamationCircle } from "react-icons/fa";
import { IoIosWarning } from "react-icons/io";
import { IoIosInformationCircle } from "react-icons/io";
import MoonLoader from "react-spinners/MoonLoader";

const getAxisTextColor = () =>
  document.querySelector('.home-theme')?.classList.contains('dark-mode') ? '#b6bfbb' : '#666';

const timestampMatchesBucket = (timestamp, selectedTimestamp, method) => {
  const date = new Date(timestamp);
  const selected = new Date(selectedTimestamp);
  if (Number.isNaN(date.getTime()) || Number.isNaN(selected.getTime())) return false;
  switch (method) {
    case "year": return date.getUTCFullYear() === selected.getUTCFullYear();
    case "month": return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth();
    case "day": return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate();
    case "hour": return date.getUTCFullYear() === selected.getUTCFullYear() && date.getUTCMonth() === selected.getUTCMonth() && date.getUTCDate() === selected.getUTCDate() && date.getUTCHours() === selected.getUTCHours();
    default: return timestamp === selectedTimestamp;
  }
};

const DynamicStringMetricChart = ({
  metric,
  catgeoryPropertyName,
  keySummary,
  selectedYearEditing,
  scenarioModels = [],
  numericTimeAggregationMethod,
  summarizedFeatureIds = [],
  applyScenarioModel = false,
  thresholdCategorical = 20,
  height = 200,
  longstandingGeoJSONRef,
  selectedKey,
  polygonCustomLayerRef,
  multuGroupSelectedArray,
  setMultuGroupSelectedArray,
  setGlobalPolygonTransparency,
  setSelectedYearEditing,
  availableDates,
  IsLoadingNewGeoJSONFromAnUpdate
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const hoveredIndexRef = useRef(null);
  const [chartType, setChartType] = useState('bar');
  const [scenarioModelledViewEnabled, setScenarioModelledViewEnabled] = useState(false);
  const multuGroupSelectedArrayRef = useRef([]);

  useEffect(() => {
    const updateAxisTextColor = (event) => {
      const chart = chartRef.current;
      if (!chart) return;
      const color = event.detail?.isDarkMode ? '#b6bfbb' : '#666';
      Object.values(chart.scales || {}).forEach((scale) => {
        scale.options.ticks.color = color;
      });
      chart.update('none');
    };
    window.addEventListener('harrier-theme-change', updateAxisTextColor);
    return () => window.removeEventListener('harrier-theme-change', updateAxisTextColor);
  }, []);

  useEffect(() => {
    multuGroupSelectedArrayRef.current = multuGroupSelectedArray;
  }, [multuGroupSelectedArray]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMouseLeave = () => {
      hoveredIndexRef.current = null;
      const hasSelection = (multuGroupSelectedArray?.length ?? 0) > 0;
      const selectedSet = hasSelection
        ? new Set(multuGroupSelectedArray)
        : null;
      fmesh.traverse((child) => {
        if (!child.isMesh || !child.material) return;
        child.material.transparent = true;
        if (!hasSelection) {
          child.material.opacity = 1;
        } else {
          const isSelected = selectedSet.has(child.name);
          child.material.opacity = isSelected ? 1 : 0.3;
        }
        child.material.needsUpdate = true;
      });
      polygonCustomLayerRef.current?.map_repaint();
    };
    canvas.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [multuGroupSelectedArray]);

  const maxIndex = useMemo(
    () => Math.max(0, availableDates.length - 1),
    [availableDates]
  );

  const marks = useMemo(() => {
    if (!longstandingGeoJSONRef.current?.features) return {};

    return availableDates.reduce((acc, date, index) => {
      const targetTime = new Date(date).getTime();

      const hasData = longstandingGeoJSONRef.current.features.some(feature => {
        const series = feature.feature_data_simple_timeseries?.[metric];
        if (!series) return false;

        return Object.entries(series).some(([ts, value]) =>
          new Date(ts).getTime() === targetTime &&
          value !== undefined &&
          value !== null &&
          value !== ""
        );
      });

      if (hasData) {
        acc[index] = new Date(date).getFullYear();
      }

      return acc;
    }, {});
  }, [availableDates, metric]);

  const selectedYearKey = useMemo(() => {
    if (!keySummary?.[metric] || !selectedYearEditing) return null;
    const selectedYear = new Date(selectedYearEditing).getFullYear();
    return Object.keys(keySummary[metric]).find(ts => {
      return new Date(ts).getFullYear() === selectedYear;
    });
  }, [keySummary, metric, selectedYearEditing]);

  const summaryForYear = useMemo(() => {
    if (!selectedYearKey) return null;
    return keySummary?.[metric]?.[selectedYearKey] || null;
  }, [keySummary, metric, selectedYearKey]);

  const hasMetricData = useMemo(() => {
    if (!summaryForYear) return false;

    return Object.keys(summaryForYear).length > 0;
  }, [summaryForYear]);

  const aggregatedData = useMemo(() => {
    if (!summaryForYear) {
      return {
        labels: [],
        organicLabels: [],
        organicData: [],
        amendedData: [],
        scenarioModelledData: [],
        hasScenarioModelledData: false,
        isIdField: false,
      };
    }
    const labels = Object.keys(summaryForYear);
    // Treat as an ID field if there are lots of unique values
    if (labels.length > 40) {
      const populated = labels.reduce(
        (sum, label) => sum + (summaryForYear[label] || 0),
        0
      );
      return {
        labels: ["Has ID"],
        organicLabels: ["Has ID"],
        organicData: [populated],
        amendedData: [populated],
        scenarioModelledData: [],
        hasScenarioModelledData: false,
        isIdField: true,
      };
    }

    const counts = new Map(labels.map((label) => [label, Number(summaryForYear[label]) || 0]));
    // IDs can arrive as numbers from the map selection and strings from stored
    // scenarios, so normalize both sides before checking whether a feature is
    // part of this chart's summary.
    const summarizedIds = new Set(summarizedFeatureIds.map(String));
    let hasScenarioModelledData = false;
    if (applyScenarioModel && summarizedIds.size > 0) {
      scenarioModels.forEach((scenario) => {
        if (scenario.aggregationMethod !== numericTimeAggregationMethod ||
            !timestampMatchesBucket(scenario.selectedYear, selectedYearEditing, numericTimeAggregationMethod)) return;
        (scenario.features || []).forEach((feature) => {
          if (!summarizedIds.has(String(feature.featureId))) return;
          if (feature.currentLandtype) {
            counts.set(feature.currentLandtype, Math.max(0, (counts.get(feature.currentLandtype) || 0) - 1));
          }
          if (feature.newLandtype) {
            counts.set(feature.newLandtype, (counts.get(feature.newLandtype) || 0) + 1);
          }
          hasScenarioModelledData = true;
        });
      });
    }
    const scenarioLabels = Array.from(new Set([...labels, ...counts.keys()]));
    return {
      labels: scenarioLabels,
      organicLabels: labels,
      organicData: labels.map((label) => Number(summaryForYear[label]) || 0),
      amendedData: scenarioLabels.map((label) => Number(summaryForYear[label]) || 0),
      scenarioModelledData: scenarioLabels.map((label) => counts.get(label) || 0),
      hasScenarioModelledData,
      isIdField: false,
    };
  }, [summaryForYear, scenarioModels, numericTimeAggregationMethod, selectedYearEditing, summarizedFeatureIds, applyScenarioModel]);

  // derive whether the currently selected timestamp has data for this metric, then render a small warning icon beside the chart.

  const metricMissingAtSelectedTimestamp = useMemo(() => {
    if (!selectedYearEditing || !longstandingGeoJSONRef.current?.features) {
      return false;
    }
    const targetTime = new Date(selectedYearEditing).getTime();
    return !longstandingGeoJSONRef.current.features.some(feature => {
      const series = feature.feature_data_simple_timeseries?.[metric];
      if (!series) return false;
      return Object.entries(series).some(([ts, value]) =>
        new Date(ts).getTime() === targetTime &&
        value !== undefined &&
        value !== null &&
        value !== ""
      );
    });
  }, [
    selectedYearEditing,
    metric,
    longstandingGeoJSONRef
  ]);

  const stringToHexColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return mapPalette[Math.abs(hash) % mapPalette.length];
  };

  useEffect(() => {
    if (
      IsLoadingNewGeoJSONFromAnUpdate ||
      !canvasRef.current ||
      !hasMetricData
    ) {
      return;
    }

    chartRef.current?.destroy();
    const ctx = canvasRef.current.getContext('2d');
    const colors = aggregatedData.labels.map(label =>
      stringToHexColor(label)
    );
    const showScenarioModelledView = applyScenarioModel &&
      aggregatedData.hasScenarioModelledData && scenarioModelledViewEnabled;
    const showScenarioView = applyScenarioModel && aggregatedData.hasScenarioModelledData;
    const chartLabels = showScenarioView ? aggregatedData.labels : aggregatedData.organicLabels;
    const chartValues = showScenarioModelledView
      ? aggregatedData.scenarioModelledData
      : showScenarioView ? aggregatedData.amendedData : aggregatedData.organicData;
    const chartColors = showScenarioView
      ? colors
      : aggregatedData.organicLabels.map(stringToHexColor);
    chartRef.current = new Chart(ctx, {
      type: chartType,
      data: {
        labels: chartLabels,
        datasets: [
          {
            label: showScenarioModelledView ? 'Scenario Modelled' : 'Organic',
            data: chartValues,
            backgroundColor: chartColors,
            borderColor: chartColors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        onHover: (event, elements, chart) => {
          // setGlobalPolygonTransparency(1)
          // if (elements.length > 0) {
          //   const index = elements[0].index;
          //   if (hoveredIndexRef.current !== index) {
          //     hoveredIndexRef.current = index;
          //     const label = chart.data.labels[index];
          //     const value = chart.data.datasets[0].data[index];
          //     const hoveredValue = chart.data.labels[index];
          //     const featureLookup = new Map(
          //       longstandingGeoJSONRef.current.features.map(feature => [
          //         String(feature.skylark_id),
          //         feature,
          //       ])
          //     );
          //     fmesh.traverse((child) => {
          //       if (!child.isMesh || !child.material) return;
          //       const feature = featureLookup.get(String(child.name));
          //       if (!feature) {
          //         child.material.transparent = true;
          //         child.material.opacity = 0;
          //         return;
          //       }
          //       const featureValue = feature?.feature_data_simple_timeseries[selectedKey][selectedYearEditing];
          //       child.material.transparent = true;
          //       child.material.opacity =
          //         featureValue === hoveredValue ? 1 : 0;
          //       child.material.needsUpdate = true;
          //     });
          //   }
          //   polygonCustomLayerRef.current.map_repaint();
          // } else {
          //   hoveredIndexRef.current = null;
          //   const hasSelection = (multuGroupSelectedArray?.length ?? 0) > 0;
          //   fmesh.traverse((child) => {
          //     if (!child.isMesh || !child.material) return;
          //     child.material.transparent = true;
          //     if (!hasSelection) {
          //       child.material.opacity = 1;
          //     } else {
          //       const selectedSet = new Set(multuGroupSelectedArray);
          //       const isSelected = selectedSet.has(child.name); // or skylark_id mapping
          //       child.material.opacity = isSelected ? 1 : 0.3;
          //     }
          //     child.material.needsUpdate = true;
          //   });
          //   polygonCustomLayerRef.current?.map_repaint();
          // }
        },
        plugins: {
          legend: {
            display: chartType === 'pie',
            position: 'right',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 6,
              boxHeight: 6,
              font: {
                size: 10,
              },
              padding: 4,
            },
          },
        },
        layout: {
          padding: {
            right: 60,
          },
        },
        ...(chartType === 'bar' && {
          scales: {
            x: {
              ticks: {
                color: getAxisTextColor(),
                display: true,
                font: {
                  size: 10,
                },
                callback: function (value) {
                  const label = this.getLabelForValue(value);
                  return label.length > 12
                    ? label.substring(0, 12) + '...'
                    : label;
                },
              },
              grid: { drawTicks: false },
              title: { display: false },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Count',
              },
              ticks: {
                color: getAxisTextColor(),
                font: { size: 9 },
              },
            },
          },
        }),
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [
    aggregatedData,
    chartType,
    scenarioModelledViewEnabled,
    applyScenarioModel,
    height,
    IsLoadingNewGeoJSONFromAnUpdate,
  ]);

  const toIsoWithOffset = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    const year = d.getUTCFullYear();
    const month = pad(d.getUTCMonth() + 1);
    const day = pad(d.getUTCDate());
    const hours = pad(d.getUTCHours());
    const minutes = pad(d.getUTCMinutes());
    const seconds = pad(d.getUTCSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
  };

  return (
    <div>
      <div
        style={{
          height: IsLoadingNewGeoJSONFromAnUpdate
            ? '100px'
            : metricMissingAtSelectedTimestamp
              ? '20px'
              : '100px',
          marginBottom: '40px',
          position: 'relative'
        }}
      >

        {IsLoadingNewGeoJSONFromAnUpdate ? (

          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MoonLoader
              size={30}
              color="#29645D"
            />
          </div>

        ) : metricMissingAtSelectedTimestamp ? (

          <div
            title="No data for this Value at this timestamp"
            style={{
              textAlign: 'left',
              zIndex: 10,
              color: '#fff',
              fontSize: '12px',
              backgroundColor: '#989898',
              paddingLeft: '10px',
              paddingTop: '10px',
              paddingBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              width: 'calc(100% - 30px)',
              borderRadius: '3px',
              marginBottom: '10px',
              marginLeft: '10px',
            }}
          >
            <IoIosInformationCircle
              color="#fff"
              size={20}
              style={{ flexShrink: 0 }}
            />

            <span
              style={{
                textAlign: 'left'
              }}
            >
              No <b>{catgeoryPropertyName}</b> data for selected<br />
              features at {' '}
              <b>
                {new Date(selectedYearEditing).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </b>
            </span>
          </div>

        ) : (

          <>
            {applyScenarioModel && aggregatedData.hasScenarioModelledData && (
              <div
                role="group"
                aria-label="Habitat chart values"
                style={{
                  position: 'absolute',
                  top: '-37.5px',
                  right: '29px',
                  display: 'inline-flex',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  zIndex: 1000000,
                }}
              >
                {[
                  { label: 'Organic', enabled: false },
                  { label: 'Scenario Modelled', enabled: true },
                ].map(({ label, enabled }) => {
                  const isActive = scenarioModelledViewEnabled === enabled;
                  return (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setScenarioModelledViewEnabled(enabled)}
                      style={{
                        border: '1px solid',
                        borderColor: enabled ? '#D63384' : '#29645D',
                        padding: '3px 7px',
                        background: isActive ? (enabled ? '#D63384' : '#29645D') : '#fff',
                        color: isActive ? '#fff' : (enabled ? '#A52265' : '#29645D'),
                        fontSize: '10px',
                        lineHeight: '14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            <button
              onClick={() =>
                setChartType(prev =>
                  prev === 'bar' ? 'pie' : 'bar',
                )
              }
              style={{
                marginBottom: '30px',
                right: '2.5px',
                float: 'right',
                position: 'absolute',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#29645D',
                zIndex: '1000000'
              }}
            >
              {chartType === 'bar' ? <FaChartPie /> : <IoBarChart />}
            </button>

            <div
              style={{
                height: chartType === 'bar' ? '140px' : '80px',
                paddingTop: chartType === 'bar' ? '0px' : '50px',
                width: '100%',
                overflow: 'hidden',
                position: 'absolute'
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  marginTop: chartType === 'bar' ? '0px' : '-50px',
                  maxHeight: '150px'
                }}
              />
            </div>
          </>

        )}

      </div>
    </div>
  );

};

export default DynamicStringMetricChart;
